import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";

import { Meal } from '../../api/meals';
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import TimePicker from "material-ui-pickers/TimePicker";
import { formatDate } from "../../util/date-utility";
import { closeAddReservationDialog, openAddPaymentDialog } from "../../actions/store/app-actions";
import {
	addReservation,
	updateSingleReservation,
	deleteReservation
} from "../../actions/reservation-actions";
import { Reservation } from "../../api/reservations";
import { Area } from "../../api/areas";
import { Table } from "../../api/tables";
import {
	fixTime,
	getReadablePaymentStatus,
	getChargeBlockReason,
	getChargeDeadline,
	isGuaranteePayment,
	canRequestGuarantee,
	paymentStateMatches
} from "../../util/general-utility";
import { format } from "../../util/date-utility";
import { conf } from "../../conf";
import { Payment } from "../../api/payments";
import { ConnectedChargeNoShowDialog } from "../payments/charge-no-show-dialog";
import { objectMatches } from "../../util/object-utility";
import { ConnectedDatePicker } from "../datepicker/index";
import { ConnectedDeleteReservationDialog } from "./delete-reservation-dialog";
import { Capabilities } from "../../integration/capabilities";
import { isIPhoneX } from '../../util/device-util';

interface State extends Reservation {
	areaSelectAnchor: HTMLElement;
	tableSelectAnchor: HTMLElement;
	selectedAreaName: string;
	selectedTableName: string;
	selectedTableId: string;
	filteredTables: Table[];
	shouldDelete: boolean;
	showChargeDialog: boolean;
	isClickable: boolean;
	adultsCount: number;
	childrenCount: number;
}

export interface AddReservationDialogProps {}

interface ConnectedAddReservationDialogProps extends WithStyles<typeof styles> {
	areas: Area[];
	tables: Table[];
	appDate: Date;
	open: boolean;
	reservationData: Reservation;
	capabilities: Capabilities;
	layout: "desktop" | "tablet" | "phone";
	availableMeals: Meal[];
	reservations: Reservation[];
	close: () => void;
	openAddPaymentDialog: (email: string, name: string, reservationId: string) => void;
}

type Props = AddReservationDialogProps &
	ConnectedAddReservationDialogProps &
	InjectedTranslateProps;

const initialState: State = {
	areaSelectAnchor: null,
	tableSelectAnchor: null,
	selectedAreaName: null,
	selectedTableName: null,
	selectedTableId: null,
	filteredTables: [],
	adultsCount: 0,
	childrenCount: 0,
	id: null,
	tableId: null,
	inquiryId: null,
	date: null,
	peopleCount: null,
	primaryName: "",
	time: null,
	phoneNumber: "",
	email: "",
	notes: "",
	meals: null,
	inquiredArea: null,
	checkedIn: false,
	shouldDelete: false,
	showChargeDialog: false,
	isClickable: false,
	payment: null
};

export class AddReservationDialog extends React.Component<Props, State> {
	public state: State = {
		...initialState,
		date: this.props.appDate
	};

	private initialReservationData: Reservation = null;
	private timer: number = null;

	componentWillReceiveProps(nextProps: Props) {
		if (this.props.open !== nextProps.open) {
			const { reservationData } = nextProps;

			if (nextProps.open) {
				this.setState({
					date: nextProps.appDate
				});

				clearTimeout(this.timer);
				this.timer = setTimeout(() => {
					this.setState({
						isClickable: true
					});
				}, 300);
			}

			if (reservationData !== null) {
				const data = {
					...reservationData,
					time: fixTime(reservationData.time)
				};

				this.initialReservationData = data;
				this.setState(data);
			}
		} else if (nextProps.open && nextProps.reservations !== this.props.reservations) {
			this.syncPayment(nextProps.reservations);
		}
	}

	// Keeps the payment up to date while the dialog is open (card requested,
	// deposited or charged). Only the payment is taken over, the other fields
	// may contain unsaved changes.
	syncPayment = (reservations: Reservation[]) => {
		const { id, payment } = this.state;

		if (!id || !reservations) {
			return;
		}

		const current = reservations.find(r => r.id === id);
		if (!current || paymentStateMatches(current.payment, payment)) {
			return;
		}

		this.setState({ payment: current.payment || null });

		if (this.initialReservationData) {
			this.initialReservationData = {
				...this.initialReservationData,
				payment: current.payment
			};
		}
	};

	close() {
		this.setState({
			...initialState,
			date: this.props.appDate
		});

		this.props.close();
	}

	handleTimeChange = (date: Date) => {
		this.setState({ time: date });
	};

	handleDateChange = (date: Date) => {
		this.setState({ date });
	};

	handlePeopleCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const val = Number(event.target.value);

		this.setState({
			peopleCount: val > 0 ? val : 0
		});
	};

	handleAdultsCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const val = Number(event.target.value);

		this.setState({
			adultsCount: val > 0 ? val : 0
		});
	};

	handleChildrenCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const val = Number(event.target.value);

		this.setState({
			childrenCount: val > 0 ? val : 0
		});
	};

	handleCancelClick = () => {
		this.close();
	};

	handleDeleteClick = () => {
		this.setState({
			shouldDelete: true
		});
	};

	handleChargeClick = () => {
		this.setState({ showChargeDialog: true });
	};

	handleChargeDialogClose = () => {
		this.setState({ showChargeDialog: false });
	};

	handleCharged = (payment: Payment) => {
		if (payment) {
			this.setState({ payment });
		}
	};

	// Why the no-show fee can not be charged right now (null if it can)
	getChargeBlockReason = () => {
		const { payment, checkedIn } = this.state;
		const saved = this.initialReservationData;

		if (saved && Boolean(saved.checkedIn) !== Boolean(checkedIn)) {
			return "Check-in wurde geändert, bitte zuerst speichern";
		}

		return getChargeBlockReason(
			payment,
			saved ? saved.date : null,
			Boolean(saved && saved.checkedIn),
			conf.payments.noShowChargeDays
		);
	};

	renderNoShowCharge = () => {
		const { classes } = this.props;
		const { payment } = this.state;

		if (!isGuaranteePayment(payment) || payment.state !== "completed") {
			return null;
		}

		if (payment.chargeState === "paid" || payment.mandateStatus === "revoked") {
			return null;
		}

		const saved = this.initialReservationData;
		const blockReason = this.getChargeBlockReason();
		const deadline = getChargeDeadline(saved ? saved.date : null, conf.payments.noShowChargeDays);

		return (
			<React.Fragment>
				<Button
					className={classNames("charge-no-show-button", classes.paymentActionButton)}
					variant="contained"
					color="primary"
					disabled={Boolean(blockReason)}
					onClick={this.handleChargeClick}
				>
					No-Show-Gebühr abbuchen
				</Button>
				<FormHelperText>
					{blockReason || (deadline ? "Abbuchen möglich bis " + format(deadline, "DD.MM.YYYY") : "")}
				</FormHelperText>
			</React.Fragment>
		);
	};

	handleRequestGuaranteeClick = () => {
		const { id, email, primaryName } = this.state;

		this.props.openAddPaymentDialog(email, primaryName, id);
	};

	// Card guarantee for an existing reservation without (valid) card
	renderGuaranteeRequest = () => {
		const { classes } = this.props;
		const { id, payment } = this.state;

		if (!id || !canRequestGuarantee(payment)) {
			return null;
		}

		return (
			<React.Fragment>
				<Button
					className={classNames("request-guarantee-button", classes.paymentActionButton)}
					variant="contained"
					color="primary"
					onClick={this.handleRequestGuaranteeClick}
				>
					Kreditkarte hinterlegen lassen
				</Button>
				<FormHelperText>
					Der Gast erhält einen Link per E-Mail
				</FormHelperText>
			</React.Fragment>
		);
	};

	handleDeletionDialogNoClick = () => {
		this.setState({
			shouldDelete: false
		});
	};

	handleDeletionDialogYesClick = () => {
		deleteReservation(this.state.id);

		this.close();
	};

	handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		const { reservationData } = this.props;

		const data = { ...this.state };
		delete data.shouldDelete;
		delete data.showChargeDialog;
		delete data.isClickable;
		delete data.payment;
		delete data.meals;

		delete data.areaSelectAnchor;
		delete data.tableSelectAnchor;
		delete data.selectedAreaName;
		delete data.selectedTableName;
		delete data.filteredTables;

		if (data.date) {
			data.date.setHours(0, 0, 0, 0);
		}

		if (reservationData == null) {
			if (data.childrenCount > 0) {
				let childrenNote =
					data.adultsCount + (data.adultsCount == 1 ? " Erwachsener" : " Erwachsene") + ", " +
					data.childrenCount + (data.childrenCount == 1 ? " Kind" : " Kinder");

				if (data.notes.length > 0) {
					data.notes = data.notes + "\n\n" + childrenNote;
				} else {
					data.notes = childrenNote;
				}
			}
			data.peopleCount = data.adultsCount + data.childrenCount;

			if(data.selectedTableId) {
				data.tableId = data.selectedTableId;
			}
		}

		delete data.adultsCount;
		delete data.childrenCount;
		delete data.selectedTableId;

		if (reservationData !== null) {
			if (objectMatches(data, this.initialReservationData) === false) {
				updateSingleReservation(data.id, data);
			}
		} else {
			addReservation(data);
		}

		this.close();
	};

	handleAreaSelectClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		this.setState({ areaSelectAnchor: event.currentTarget });
	};

	handleAreaSelectClose = () => {
		this.setState({ areaSelectAnchor: null });
	};

	handleTableSelectClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		this.setState({ tableSelectAnchor: event.currentTarget });
	};

	handleTableSelectClose = () => {
		this.setState({ tableSelectAnchor: null });
	};

	handleAreaSelectItemClick = (id: string) => {
		const { tables } = this.props;
		const newFilteredTables = tables.filter(table => table.areaId === id);

		this.setState({
			filteredTables: newFilteredTables
		});
		this.setSelectedAreaName(id);
		this.resetSelectedTable(id, newFilteredTables); 
	};

	setSelectedAreaName = (id: string) => {
		const { areas } = this.props;
		if (!id) {
			this.setState({
				selectedAreaName: null
			});
			return;
		}

		const activeArea = areas.find(area => area.id === id);
		if (activeArea) {
			this.setState({
				selectedAreaName: activeArea.name
			});
		}
	};

	resetSelectedTable = (areaId: string, newFilteredTables: Table[]) => { 
		const { filteredTables } = this.state;
		if (areaId && newFilteredTables.length > 0) {
			this.setState({
				selectedTableId: newFilteredTables[0].id,
				selectedTableName: newFilteredTables[0].displayName
			});
			return;
		}

		this.setState({
			selectedTableId: null,
			selectedTableName: null
		});
	};

	handleTableSelectItemClick = (id: string) => {
		this.setState({
			selectedTableId: id
		});
		this.setSelectedTableName(id);
	};

	setSelectedTableName = (id: string) => {
		const { tables } = this.props;

		const activeTable = tables.find(table => table.id === id);

		if (activeTable) {
			this.setState({
				selectedTableName: activeTable.displayName
			});
		}
	};

	render() {
		const {
			tables,
			areas,
			classes,
			t,
			open,
			reservationData,
			layout,
			capabilities,
			availableMeals
		} = this.props;
		const {
			selectedAreaName,
			selectedTableName,
			areaSelectAnchor,
			tableSelectAnchor,
			filteredTables,
			peopleCount,
			date,
			primaryName,
			time,
			phoneNumber,
			email,
			notes,
			checkedIn,
			shouldDelete,
			showChargeDialog,
			isClickable,
			payment,
			meals,
			adultsCount,
			childrenCount
		} = this.state;

		var isEditMode = Boolean(reservationData);

		return (
			<React.Fragment>
				<Dialog
					className={classNames(classes.dialog, {
						[classes.dialogClickable]: isClickable
					})}
					open={open}
					fullScreen={layout === "phone"}
				>
					<form className={classes.form} onSubmit={this.handleSubmit}>
						<DialogTitle>
							{isEditMode
								? "Reservierung bearbeiten"
								: "Neue Reservierung"}
						</DialogTitle>
						<DialogContent>
						{!isEditMode && (
								<div>
									<div className={classNames(classes.formRow)}> 
										<InputLabel
											className={classNames(classes.inputLabel, {
												[classes.hide]: !Boolean(selectedAreaName)
											})}
											shrink={true}>
											Bereich
										</InputLabel> 
										<InputLabel 
											className={classNames(classes.inputLabel, classes.hideOnMobile, {
												[classes.hide]: !Boolean(selectedTableName)
											})}
											shrink={true}>
											Tisch
										</InputLabel>  
									</div>
									<div className={classNames(classes.formRow)}> 
										<Button
											className={classNames(classes.buttonField, {
												[classes.colorGrey]: !Boolean(selectedAreaName)
											})}
											variant="flat"
											size="large"
											color="inherit"
											onClick={this.handleAreaSelectClick}
										>
											{selectedAreaName
											? selectedAreaName
											: "Bereich"}
										</Button>
										<Menu   
											anchorEl={areaSelectAnchor}
											open={Boolean(areaSelectAnchor)}
											onClick={this.handleAreaSelectClose}
										> 
											<MenuItem
												key={null}
												onClick={() =>
													this.handleAreaSelectItemClick(null)
												}
											>
												Keine Auswahl
											</MenuItem>
											{areas.map(area => (
												<MenuItem
													key={area.id}
													onClick={() =>
														this.handleAreaSelectItemClick(area.id)
													}
												>
													{area.name}
												</MenuItem>
											))}
										</Menu> 				
										<Button 
											className={classNames(classes.buttonField, {
												[classes.colorGrey]: !Boolean(selectedTableName)
											})}
											variant="flat"
											size="large"
											color="inherit" 
											disabled={!Boolean(selectedAreaName)}
											onClick={this.handleTableSelectClick}
										>
											{selectedTableName
											? selectedTableName
											: "Tisch"} 
										</Button>
										<Menu   
											anchorEl={tableSelectAnchor}
											open={Boolean(tableSelectAnchor)}
											onClick={this.handleTableSelectClose}
										> 
											{filteredTables.map(table => (
												<MenuItem
													key={table.id}
													onClick={() =>
														this.handleTableSelectItemClick(table.id)
													}
												>
													{table.displayName}
												</MenuItem>
											))}
										</Menu>	
									</div>
								</div>
							)}
							{!isEditMode && (
								<div className={classNames(classes.formRow)}>
									<TextField
										type="number"
										className={classNames(
											classes.textField
										)}
										onChange={this.handleAdultsCountChange}
										id="reservationAdultsCount"
										label="Erwachsene"
										value={adultsCount ? adultsCount : ""}
										required
									/>
									<TextField
										type="number"
										className={classNames(
											classes.textField
										)}
										onChange={this.handleChildrenCountChange}
										id="reservationChildrenCount"
										label="Kinder"
										value={childrenCount ? childrenCount : ""}
									/>
								</div>
							)}
							<div className={classNames(classes.formRow)}>
								{isEditMode && (
									<TextField
										type="number"
										className={classNames(
											classes.textField,
											classes.peopleCountInput
										)}
										id="reservationPeopleCount"
										label="Personenanzahl"
										value={peopleCount ? peopleCount : ""}
										onChange={this.handlePeopleCountChange}
										required
									/>
								)}
								<TextField
									className={classNames(
										classes.textField,
										classes.primaryNameInput
									)}
									id="reservationPrimaryName"
									fullWidth={!isEditMode}
									label="Name"
									value={primaryName}
									onChange={event =>
										this.setState({
											primaryName: event.target.value
										})
									}
									required
								/>
							</div>
							<div className={classes.formRow}>
								<ConnectedDatePicker
									onChange={this.handleDateChange}
									value={date}
									datepickerProps={{
										id: "reservationDate",
										label: "Datum",
										required: true,
										className: classNames(
											classes.textField,
											classes.datepicker
										),
										InputProps: {
											value: formatDate(date)
										}
									}}
								/>
								<TimePicker
									className={classNames(
										classes.textField,
										classes.timepicker
									)}
									id="reservationTime"
									label="Uhrzeit"
									cancelLabel="Abbrechen"
									okLabel="OK"
									value={time}
									onChange={this.handleTimeChange}
									ampm={false}
								/>
							</div>
							<div className={classes.formRow}>
								<TextField
									className={classNames(
										classes.textField,
										classes.phoneNumberInput
									)}
									id="reservationPhoneNumber"
									label="Telefonnummer"
									value={phoneNumber}
									onChange={event =>
										this.setState({
											phoneNumber: event.target.value
										})
									}
								/>
								<TextField
									className={classNames(
										classes.textField,
										classes.emailInput
									)}
									id="reservationEmail"
									label="E-Mail Adresse"
									value={email}
									type="email"
									onChange={event =>
										this.setState({
											email: event.target.value
										})
									}
								/>
							</div>
							<div className={classes.formRow}>
								<TextField
									className={classNames(
										classes.textField,
										classes.notesInput
									)}
									id="reservationNotes"
									label="Notizen &amp; Anmerkungen"
									fullWidth
									multiline
									rowsMax={4}
									value={notes}
									onChange={event =>
										this.setState({
											notes: event.target.value
										})
									}
								/>
							</div>
							{meals && Object.keys(meals).length > 0 && (
								<div className={classes.formRow}>
									<FormGroup className={classes.textField}>
										<FormLabel className={classes.formLabel}>Speisen</FormLabel>
										<div className={classes.formText}>
											{Object.keys(meals).map(id => (
												<div key={id}>{meals[id]}x {availableMeals.find(m => m.id === id).name}</div>
											))}
										</div>
									</FormGroup>
								</div>
							)}
							{isEditMode && (payment || canRequestGuarantee(payment)) && (
								<div className={classes.formRow}>
									<FormGroup className={classes.textField}>
										<FormLabel className={classes.formLabel}>Zahlung</FormLabel>
										{payment && (
											<div className={classes.formText}>
												<span className={classNames(classes.paymentState, {
													[classes.paymentNotCompleted]: payment.state !== "completed",
													[classes.paymentCharged]: payment.chargeState === "paid",
												})}>
													{getReadablePaymentStatus(payment)}
												</span>
											</div>
										)}
										{this.renderNoShowCharge()}
										{this.renderGuaranteeRequest()}
									</FormGroup>
								</div>
							)}
							<div className={classes.formRow}>
								<FormGroup
									className={classNames(
										classes.textField
									)}
								>
									<FormControlLabel
										control={
											<Checkbox
												id="checkedIn"
												color="primary"
												checked={checkedIn}
												onChange={event =>
													this.setState({
														checkedIn: event.target.checked
													})
												}
											/>
										}
										label="Eingecheckt"
									/>
								</FormGroup>
							</div>
						</DialogContent>
						<DialogActions>
							{isEditMode && (
								<Button
									className={classes.deleteButton}
									onClick={this.handleDeleteClick}
								>
									Löschen
								</Button>
							)}
							<Button
								className={classes.cancelButton}
								onClick={this.handleCancelClick}
							>
								Abbrechen
							</Button>
							<Button
								className={classes.submitButton}
								color="primary"
								type="submit"
							>
								{isEditMode ? "Speichern" : "OK"}
							</Button>
						</DialogActions>
					</form>
				</Dialog>
				{isGuaranteePayment(payment) && (
					<ConnectedChargeNoShowDialog
						open={showChargeDialog}
						payment={payment}
						reservationDate={this.initialReservationData ? this.initialReservationData.date : null}
						checkedIn={Boolean(this.initialReservationData && this.initialReservationData.checkedIn)}
						onClose={this.handleChargeDialogClose}
						onCharged={this.handleCharged}
					/>
				)}
				<ConnectedDeleteReservationDialog
					open={shouldDelete}
					peopleCount={peopleCount}
					primaryName={primaryName}
					onNo={this.handleDeletionDialogNoClick}
					onYes={this.handleDeletionDialogYesClick}
				/>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	areas: state.areas,
	tables: state.tables,
	appDate: state.date,
	open: state.addReservationDialogOpen,
	reservationData: state.reservationDialogReservationData,
	capabilities: state.capabilities,
	layout: state.layout,
	availableMeals: state.settings.meals,
	reservations: state.reservations
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	close: () => dispatch(closeAddReservationDialog()),
	openAddPaymentDialog: (email: string, name: string, reservationId: string) =>
		dispatch(openAddPaymentDialog(undefined, email, name, undefined, undefined, undefined, undefined, reservationId))
});

const styles = (theme: Theme) =>
	createStyles({
		dialog: {
			"-webkit-user-select": "none",
			userSelect: "none",
			pointerEvents: "none"
		},
		dialogClickable: {
			pointerEvents: "all"
		},
		textField: {
			margin: 10,

			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		},
		buttonField: {
			margin: 10,
			border: 0,
			display: "inline-flex",
			padding: 0,
			position: "relative",
			minWidth: 0,
			justifyContent: "left",
			flexDirection: "row",
			alignItems: "center",
			color: "currentColor",
			fontWeight: "inherit",
			borderBottom: "1px solid rgb(148 148 148)",
			borderRadius: 0,
			flex: "1 1 0px",
			textTransform: "none", 
			"&:hover": {
				borderBottomColor: "black",
			},
			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		}, 
		inputLabel: {
			paddingLeft: 10,
			paddingTop: 10,
			display: "inline-flex",
			flex: "1 1 0px",
			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		},
		colorGrey: {
			color: "rgba(0, 0, 0, 0.54)"
		},
		hide:{
			visibility: "hidden"
		},
		hideOnMobile:{
			"@media (max-width: 600px)": {
				display: "none"
			}
		},
		form: {
			height: "100%",
			display: "flex",
			flexDirection: "column"
		},
		formRow: {
			display: "flex",
			margin: -10,

			"@media (max-width: 600px)": {
				display: "block"
			}
		},
		peopleCountInput: {},
		datepicker: {},
		primaryNameInput: {},
		timepicker: {},
		phoneNumberInput: {},
		emailInput: {},
		notesInput: {},
		cancelButton: {},
		deleteButton: {
			marginRight: "auto"
		},
		submitButton: {},
		formLabel: {
			paddingTop: "5px",
			transform: "translate(0, 1.5px) scale(0.75)",
			transformOrigin: "top left"
		},
		formText: {
			paddingTop: "2px",
			color: "rgba(0, 0, 0, 0.87)",
			fontSize: "1.1428571428571428rem",
			fontFamily: "'Roboto', 'Helvetica', 'Arial', 'sans-serif'",
			lineHeight: "1.1875em"
		},
		paymentState: { color: "green" },
		paymentNotCompleted: { color: "black" },
		paymentCharged: { color: theme.status.reservation.charged },
		// Same left edge as the other form elements
		paymentActionButton: {
			alignSelf: "flex-start",
			marginTop: 8
		}
	});

export const ConnectedAddReservationDialog = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(AddReservationDialog)
	)
);
